/**
 * 언어 토글 (JA / KO) — Layout 헤더 우측 상단.
 *
 * UX 결정:
 *   - 두 언어만 지원하므로 dropdown 보다 토글이 가볍고 직관적.
 *   - 현재 선택된 언어는 색상으로 강조 (active = blue-50 배경).
 *   - 클릭 시 ``i18n.changeLanguage`` 호출 → detector 가 localStorage 에
 *     자동 persist (``LANG_STORAGE_KEY`` 키).
 *   - 접근성: ``role="group"`` + 각 버튼 ``aria-pressed`` 상태. 키보드
 *     Tab + Enter 만으로도 전환 가능.
 *   - 표시 라벨은 두 언어 모두에서 자국명 노출 ("日本語" / "한국어")
 *     이 자연스럽다 (외국인이 자기 언어를 알아볼 수 있도록).
 */

import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";

import { SUPPORTED_LANGS, type SupportedLang } from "./index";

interface Props {
  className?: string;
}

export default function LanguageToggle({ className }: Props) {
  const { i18n, t } = useTranslation();

  // i18n.resolvedLanguage 는 detector / changeLanguage 결과를
  // ``supportedLngs`` 와 ``nonExplicitSupportedLngs`` 규칙으로 정규화한
  // 값 (예: "ko-KR" → "ko"). UI 비교는 항상 이 값을 사용한다.
  const current = (i18n.resolvedLanguage ?? "ja") as SupportedLang;

  return (
    <div
      role="group"
      aria-label={t("lang.switchTo")}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1 py-0.5",
        className,
      )}
    >
      <Globe className="ml-1 h-3 w-3 text-slate-400" aria-hidden />
      {SUPPORTED_LANGS.map((lng) => {
        const active = current === lng;
        return (
          <button
            key={lng}
            type="button"
            onClick={() => {
              if (!active) void i18n.changeLanguage(lng);
            }}
            aria-pressed={active}
            aria-label={t(`lang.${lng}`)}
            className={cn(
              "rounded px-1.5 py-0.5 text-[11px] font-medium transition",
              active
                ? "bg-blue-50 text-blue-700"
                : "text-slate-500 hover:bg-slate-50 hover:text-slate-700",
            )}
          >
            {t(`lang.${lng}`)}
          </button>
        );
      })}
    </div>
  );
}
